// React
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

//Futurelab
import { ChatListChatRequestLoop } from './ChatListChatRequestLoop';
import { ChatListDataInterface, ChatParticipantsInterface } from './ChatListItemsMobile';
import { ChatList } from './ChatList';
import { RootState } from '../../reducers';
import { userListDetails } from '../../actions/userListDetailsActions';
import { loggedInUserDetails } from '../../actions/loggedInUserDetailsActions';
import { ChatRoomItemsInterface } from './ChatRoom';
import { loadPendingApprovalChatDetails } from '../../actions/pendingApprovalActions';
import { hideLoaderAction, showLoaderAction } from '../../actions/loaderActions';
import { chatList } from '../../actions/chatListActions';
import { getAllParticipantsIds } from '../../utilities/Chat';
import { useLazyGetChatListQuery, useLazyGetContactsQuery, useLazyGetPendingChatsQuery } from '../../store/api/chatApi';
import { userApi } from '../../store/api/userApi';
import { store } from '../../ReduxRoot';
import { getMissingUserIds, getUserDisplayName, returnOneToOneChatUserDetails } from '../../utilities/UserDetails';

import { Input } from '../ui/input';
import { ArrowDownAZ, ArrowUpAZ, X } from 'lucide-react';

// 3rd party
import _ from 'lodash';

const CHAT_PAGE_SIZE = 25;

const contactDisplayName = (
	chat: ChatListDataInterface,
	loggedInUserDetail: any,
	profiles: any[]
) => {
	if (chat.isGroupChat) return chat.name?.trim() || 'Unnamed group';
	return getUserDisplayName(returnOneToOneChatUserDetails(chat, loggedInUserDetail, profiles));
};

const sortContacts = (
	contacts: ChatListDataInterface[],
	sort: 'asc' | 'desc',
	loggedInUserDetail: any,
	profiles: any[]
) =>
	[...contacts].sort((left, right) => {
		const comparison = contactDisplayName(left, loggedInUserDetail, profiles).localeCompare(
			contactDisplayName(right, loggedInUserDetail, profiles),
			undefined,
			{ sensitivity: 'base' }
		);
		return sort === 'asc' ? comparison : -comparison;
	});

export interface PinnedMessageItem {
	chatId: ChatListDataInterface;
	chatMessageId: ChatRoomItemsInterface;
	id: number;
	messageSenderId: ChatParticipantsInterface;
	participantId: ChatParticipantsInterface;
}

export function ChatListMenu() {
	// redux store setup
	const userListProfilesDetails = useSelector((state: RootState) => state.userListProfilesDetails);
	const loggedInUserDetail = useSelector((state: RootState) => state.loggedInUserDetails);
	const chatInFocus = useSelector((state: RootState) => state.chatInFocus);
	const chatListsItems = useSelector((state: RootState) => state.chatList);
	const pendingApprovalChatList = useSelector((state: RootState) => state.pendingApprovalChatList);

	// useState initilizations
	const [value, setValue] = useState('1');
	const [contactSort, setContactSort] = useState<'asc' | 'desc'>('asc');
	const [filterQuery, setFilterQuery] = useState('');
	const dispatch = useDispatch();
	const [loadChatList] = useLazyGetChatListQuery();
	const [loadContacts] = useLazyGetContactsQuery();
	const [loadPendingChats] = useLazyGetPendingChatsQuery();
	const [hasMoreChats, setHasMoreChats] = useState(true);
	const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false);
	const [contacts, setContacts] = useState<ChatListDataInterface[]>([]);
	const [hasMoreContacts, setHasMoreContacts] = useState(true);
	const [isLoadingMoreContacts, setIsLoadingMoreContacts] = useState(false);
	const chatListRef = useRef<ChatListDataInterface[]>([]);
	const nextOffsetRef = useRef(0);
	const isLoadingMoreRef = useRef(false);
	const nextContactsOffsetRef = useRef(0);
	const isLoadingMoreContactsRef = useRef(false);
	const hasInitialized = useRef(false);
	const loadMissingProfiles = async (userIds: Array<number | string>) => {
		const missingUserIds = getMissingUserIds(userIds, store.getState().userListProfilesDetails);
		if (missingUserIds.length === 0) return;
		const users = await store.dispatch(userApi.endpoints.getUserDetailsList.initiate(missingUserIds)).unwrap();
		dispatch(userListDetails(users));
	};

	useEffect(() => {
		chatListRef.current = chatListsItems;
	}, [chatListsItems]);

	useEffect(() => {
		if (hasInitialized.current) {
			return;
		}
		hasInitialized.current = true;

		const fetchDataAsync = async () => {
			dispatch(showLoaderAction());
			try {
				const [messagesResult, contactsResult, pendingChatsResult, currentUserResult] = await Promise.allSettled([
					loadChatList({ offset: 0, limit: CHAT_PAGE_SIZE }).unwrap(),
					loadContacts({ offset: 0, limit: CHAT_PAGE_SIZE, sort: contactSort }).unwrap(),
					loadPendingChats().unwrap(),
					_.isEmpty(loggedInUserDetail)
						? store.dispatch(userApi.endpoints.getCurrentUser.initiate()).unwrap()
						: Promise.resolve(null),
				]);
				const initialChats = messagesResult.status === 'fulfilled' ? messagesResult.value : [];
				const initialContacts = contactsResult.status === 'fulfilled' ? contactsResult.value : [];
				if (messagesResult.status === 'rejected') {
					console.warn('Unable to load chats:', messagesResult.reason);
				}
				if (contactsResult.status === 'rejected') {
					console.warn('Unable to load contacts:', contactsResult.reason);
				}
				dispatch(chatList(initialChats));
				nextOffsetRef.current = initialChats.length;
				setHasMoreChats(initialChats.length === CHAT_PAGE_SIZE);
				setContacts(initialContacts);
				nextContactsOffsetRef.current = initialContacts.length;
				setHasMoreContacts(initialContacts.length === CHAT_PAGE_SIZE);

				const pendingChats = pendingChatsResult.status === 'fulfilled' ? pendingChatsResult.value : [];
				if (pendingChatsResult.status === 'fulfilled') {
					dispatch(loadPendingApprovalChatDetails(pendingChats));
				} else {
					console.warn('Unable to load pending chats:', pendingChatsResult.reason);
				}
				if (currentUserResult.status === 'fulfilled' && currentUserResult.value) {
					dispatch(loggedInUserDetails(currentUserResult.value));
				} else if (currentUserResult.status === 'rejected') {
					console.warn('Unable to load the current user:', currentUserResult.reason);
				}

				await loadMissingProfiles([
					...getAllParticipantsIds([...initialChats, ...initialContacts], pendingChats),
				]);
				setContacts(sortContacts(initialContacts, contactSort, store.getState().loggedInUserDetails, store.getState().userListProfilesDetails));
			} catch (error) {
				console.warn('Error:', error);
			} finally {
				dispatch(hideLoaderAction());
			}
		};

		fetchDataAsync();
	}, []);

	const handleContactSortChange = async (sort: 'asc' | 'desc') => {
		if (sort === contactSort || isLoadingMoreContactsRef.current) return;

		setContactSort(sort);
		isLoadingMoreContactsRef.current = true;
		setIsLoadingMoreContacts(true);
		try {
			const sortedContacts = await loadContacts({ offset: 0, limit: CHAT_PAGE_SIZE, sort }).unwrap();
			setContacts(sortedContacts);
			nextContactsOffsetRef.current = sortedContacts.length;
			setHasMoreContacts(sortedContacts.length === CHAT_PAGE_SIZE);
			await loadMissingProfiles(getAllParticipantsIds(sortedContacts, []));
			setContacts(sortContacts(sortedContacts, sort, store.getState().loggedInUserDetails, store.getState().userListProfilesDetails));
		} catch (error) {
			console.warn('Unable to sort contacts:', error);
		} finally {
			isLoadingMoreContactsRef.current = false;
			setIsLoadingMoreContacts(false);
		}
	};

	const loadNextChatPage = async () => {
		if (!hasMoreChats || isLoadingMoreRef.current) {
			return;
		}

		isLoadingMoreRef.current = true;
		setIsLoadingMoreChats(true);
		try {
			const nextChats = await loadChatList({ offset: nextOffsetRef.current, limit: CHAT_PAGE_SIZE }).unwrap();
			const chatsById = new Map(chatListRef.current.map((chat) => [chat.id, chat]));
			nextChats.forEach((chat) => chatsById.set(chat.id, chat));
			dispatch(chatList(Array.from(chatsById.values())));
			nextOffsetRef.current += nextChats.length;
			setHasMoreChats(nextChats.length === CHAT_PAGE_SIZE);

			await loadMissingProfiles(getAllParticipantsIds(nextChats, []));
		} catch (error) {
			console.warn('Error:', error);
		} finally {
			isLoadingMoreRef.current = false;
			setIsLoadingMoreChats(false);
		}
	};

	const loadNextContactPage = async () => {
		if (!hasMoreContacts || isLoadingMoreContactsRef.current) {
			return;
		}

		isLoadingMoreContactsRef.current = true;
		setIsLoadingMoreContacts(true);
		try {
			const nextContacts = await loadContacts({ offset: nextContactsOffsetRef.current, limit: CHAT_PAGE_SIZE, sort: contactSort }).unwrap();
			setContacts((currentContacts) => {
				const contactsById = new Map(currentContacts.map((chat) => [chat.id, chat]));
				nextContacts.forEach((chat) => contactsById.set(chat.id, chat));
				return Array.from(contactsById.values());
			});
			nextContactsOffsetRef.current += nextContacts.length;
			setHasMoreContacts(nextContacts.length === CHAT_PAGE_SIZE);

			await loadMissingProfiles(getAllParticipantsIds(nextContacts, []));
			setContacts((currentContacts) => sortContacts(currentContacts, contactSort, store.getState().loggedInUserDetails, store.getState().userListProfilesDetails));
		} catch (error) {
			console.warn('Error:', error);
		} finally {
			isLoadingMoreContactsRef.current = false;
			setIsLoadingMoreContacts(false);
		}
	};

	useEffect(() => {
		const selectedChat = chatListsItems.find((chat) => chat.id === chatInFocus?.id);
		if (selectedChat && (selectedChat.unreadCount ?? 0) > 0) {
			dispatch(
				chatList(chatListsItems.map((chat) => (chat.id === selectedChat.id ? { ...chat, unreadCount: 0 } : chat)))
			);
		}
	}, [chatInFocus, chatListsItems, dispatch]);

	const newMessageExists = (): boolean => {
		return chatListsItems.some((chatListsItem) => (chatListsItem.unreadCount ?? 0) > 0);
	};
	const newChatRequestExists = (): boolean => {
		return pendingApprovalChatList.length > 0;
	};

	return (
		<div className="flex h-full w-full min-w-0 flex-col overflow-hidden">
			<div className="relative w-full p-3">
				<Input
					className="h-10 pr-9"
					placeholder="Search messages or chat"
					value={filterQuery}
					onChange={(event) => setFilterQuery(event.target.value)}
				/>
				{filterQuery && (
					<button
						aria-label="Clear search"
						className="absolute right-5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						onClick={() => setFilterQuery('')}
						type="button"
					>
						<X className="size-4" />
					</button>
				)}
			</div>
			<div className="flex h-12 w-full border-b border-slate-200" role="tablist" aria-label="Chat categories">
				<button
					aria-selected={value === '1'}
					className={`relative flex min-w-0 flex-1 cursor-pointer items-center justify-center border-b-2 px-2 text-sm font-medium transition-colors ${value === '1' ? 'border-primary text-primary' : 'border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
					onClick={() => setValue('1')}
					role="tab"
					type="button"
				>
						<span className="relative">
							Messages
							{newMessageExists() && <span className="absolute -right-2 -top-1 size-1.5 rounded-full bg-destructive" />}
						</span>
				</button>
				<button
					aria-selected={value === '2'}
					className={`relative flex min-w-0 flex-1 cursor-pointer items-center justify-center border-b-2 px-2 text-sm font-medium transition-colors ${value === '2' ? 'border-primary text-primary' : 'border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
					onClick={() => setValue('2')}
					role="tab"
					type="button"
				>
					Contacts
				</button>
				<button
					aria-selected={value === '3'}
					className={`relative flex min-w-0 flex-1 cursor-pointer items-center justify-center border-b-2 px-2 text-sm font-medium transition-colors ${value === '3' ? 'border-primary text-primary' : 'border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
					onClick={() => setValue('3')}
					role="tab"
					type="button"
				>
					<span className="relative">
						Request
						{newChatRequestExists() && <span className="absolute -right-2 -top-1 size-1.5 rounded-full bg-destructive" />}
					</span>
				</button>
			</div>
			<div className="min-h-0 min-w-0 flex-1 overflow-hidden">
				{value === '1' && (
					<div role="tabpanel">
					<ChatList
						userListProfilesDetails={userListProfilesDetails}
						loggedInUserDetail={loggedInUserDetail}
						chatListsItems={chatListsItems}
						filterQuery={filterQuery}
						setFilterQuery={setFilterQuery}
						callbackScroll={loadNextChatPage}
						hasMore={hasMoreChats}
						isLoadingMore={isLoadingMoreChats}
						emptyActionLabel="Open contacts"
						onEmptyAction={() => setValue('2')}
					/>
					</div>
				)}
				{value === '3' && (
					<div role="tabpanel">
					<ChatListChatRequestLoop
						filterQuery={filterQuery}
						userListProfilesDetails={userListProfilesDetails}
						loggedInUserDetail={loggedInUserDetail}
					/>
					</div>
				)}
				{value === '2' && (
					<div className="flex h-full min-h-0 flex-col" role="tabpanel">
						<div className="flex h-11 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4">
							<span className="text-sm font-semibold text-slate-800">All contacts</span>
							<div aria-label="Sort contacts by name" className="inline-flex rounded-lg bg-slate-100 p-0.5" role="group">
								<button
									aria-label="Sort contacts from A to Z"
									aria-pressed={contactSort === 'asc'}
									className={`inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${contactSort === 'asc' ? 'bg-white text-primary shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
									disabled={isLoadingMoreContacts}
									onClick={() => handleContactSortChange('asc')}
									type="button"
								>
									<ArrowDownAZ aria-hidden="true" className="size-3.5" />
									A–Z
								</button>
								<button
									aria-label="Sort contacts from Z to A"
									aria-pressed={contactSort === 'desc'}
									className={`inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${contactSort === 'desc' ? 'bg-white text-primary shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
									disabled={isLoadingMoreContacts}
									onClick={() => handleContactSortChange('desc')}
									type="button"
								>
									<ArrowUpAZ aria-hidden="true" className="size-3.5" />
									Z–A
								</button>
							</div>
						</div>
					<ChatList
						userListProfilesDetails={userListProfilesDetails}
						loggedInUserDetail={loggedInUserDetail}
						filterQuery={filterQuery}
						setFilterQuery={setFilterQuery}
						chatListsItems={contacts}
						callbackScroll={loadNextContactPage}
						hasMore={hasMoreContacts}
						isLoadingMore={isLoadingMoreContacts}
						showMessagePreview={false}
						scrollViewportClassName="h-[calc(100dvh-217px)] md:h-[calc(100dvh-217px)]"
					/>
					</div>
				)}
			</div>
		</div>
	);
}
